import { useEffect, useState } from 'react'
import { LuTrash, LuKeyRound, LuPlus } from 'react-icons/lu'
import { Link, useNavigate, useParams } from 'react-router-dom'
import useMergedData from '../hooks/useMergedData'
import useNetwork from '../models/useNetwork'
import useCloseWhenMissing, { HOME } from '../hooks/useCloseWhenMissing'
import useUsage, { periodLabel, sumTraffic, totalTraffic } from '../models/useUsage'
import useChannel from '../models/useChannel'
import useNode, { isNodeOffline } from '../models/useNode'
import useNodeAttach from '../models/useNodeAttach'
import useToken from '../models/useToken'
import ListItemLink from './ListItemLink'
import DetachButton from './DetachButton'
import ScrollList from './ScrollList'
import StorageSection from './StorageSection'
import TokenIndicator from './TokenIndicator'
import DeleteConfirm from './DeleteConfirm'
import RenameTitle from './RenameTitle'
import ChannelSelect from './ChannelSelect'
import BackListItem from './BackListItem'
import TokenInsert from './TokenInsert'
import TrafficMatrix from './TrafficMatrix'
import TrafficAllowance from './TrafficAllowance'
import NetworkAccess from './NetworkAccess'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import IconNode from './IconNode'
import Stack from '@mui/material/Stack'
import List from '@mui/material/List'
import Box from '@mui/material/Box'
import { useOrgRole } from '../hooks/useOrgRole'
import { NETWORK_FORBIDDEN, toolRefusalMessage } from '../utilities/routerWrites'
import Alert from '@mui/material/Alert'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import useOrganizations from '../models/useOrganizations'
import { getUserId } from '../lib/workos'
import { mayAddNodes, type OrgRole } from '../utilities/orgRole'
import { nodePagePath } from '../utilities/nodeGroups'
import { nodeNameIn } from '../utilities/nodeNetworks'
import { countLabel } from '../utilities/nodeStatus'
import { NETWORK_DELETE_SHARING_LINE } from '../utilities/nodeAttach'
import {
  ADD_NODE_REFUSED,
  isolationLine,
  membersMayAddNodesLine,
} from '../utilities/networkSettings'

/** The height of one member row, in px, as the harness measures it (`?panel=pages&multi=1`). */
const MEMBER_ROW_HEIGHT = 48

export default function Network() {
  const model = useNetwork()
  const navigate = useNavigate()
  const { networkId } = useParams()
  const { networks, nodes } = useMergedData()
  const { tokens, ...tokenModel } = useToken()
  const { channels } = useChannel()
  const { can, controls, role } = useOrgRole()
  const loadUsage = useUsage(state => state.get)
  const usage = useUsage(state => state.networks.find(n => n.networkId === networkId))
  const period = useUsage(state => state.period)
  const loadAllTraffic = useUsage(state => state.loadAllTraffic)
  const nodeTrafficAll = useUsage(state => state.nodeTrafficAll)
  const activeOrgId = useOrganizations(s => s.activeOrgId)
  // The refusal of the last channel change or key creation, as one sentence (app#373).
  const [channelError, setChannelError] = useState<string | null>(null)
  const [keyError, setKeyError] = useState<string | null>(null)
  const [isolationError, setIsolationError] = useState<string | null>(null)
  const [isolationBusy, setIsolationBusy] = useState(false)
  const [addNodesError, setAddNodesError] = useState<string | null>(null)
  const [addNodesBusy, setAddNodesBusy] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  // Network traffic = the sum of its nodes' directional legs (get_node_traffic across the org).
  const networkTraffic = nodeTrafficAll ? sumTraffic(nodeTrafficAll.filter(t => t.networkId === networkId)) : null
  // Depend on activeOrgId: both loaders early-return on a null org, and on a hard refresh this route
  // mounts before the org store hydrates — re-run once the org lands so Traffic doesn't stay empty.
  useEffect(() => {
    if (!activeOrgId) return
    void loadUsage()
    void loadAllTraffic()
  }, [activeOrgId, loadUsage, loadAllTraffic])
  const { id, name, channelId, ownerId, isolated, membersMayAddNodes } = networks.find(n => n.id === networkId) || {}
  // ⚠ A network that is not a network of the active org closes this page (app#473), and the
  // reader goes to the org's home view.
  useCloseWhenMissing({
    id: networkId,
    found: !!id,
    loadedOrgId: useNetwork(state => state.loadedOrgId),
    orgOf: useNetwork(state => state.orgOf),
    to: HOME,
  })
  // ⚠⚠ THE NETWORK'S OWNER OR AN ORG OWNER/ADMIN manages it: rename, channel, keys and delete
  // (app#373, docs/PERMISSIONS.md § 5 Network). Rename and channel were Admin only here, so a Member
  // could not rename the network they created. The router's `network_update` and `network_key_*`
  // ask the same rule; this only hides the controls.
  const canManage = controls(ownerId)
  const networkTokens = tokens.filter(t => t.networkId === id)
  const includedNodes = nodes.filter(n => !!id && n.networkIds.includes(id))
  const members = [...includedNodes].sort((a, b) => nodeNameIn(a, id).localeCompare(nodeNameIn(b, id)))
  const openAttach = useNodeAttach(state => state.open)

  const memberCount = { total: includedNodes.length, online: includedNodes.filter(n => !isNodeOffline(n)).length }
  const nodesComplete = useNode(state => state.nodesComplete)
  // ⚠ "Members may add nodes" (docs/PERMISSIONS.md § 5 Attachment, app#376): the network's owner or
  // an org Owner/Admin always adds nodes, any other member only while the setting is on. The
  // router's `RequireAttach` asks the same rule at provision and registration.
  const canAddNodes = mayAddNodes(role as OrgRole, ownerId, getUserId(), membersMayAddNodes)
  // ⚠ Leave the page only when the delete worked. A refused delete keeps the network, and the page
  // says why under the button.
  const onDelete = async () => {
    if (!id) return
    setDeleteError(null)
    const refusal = await model.delete(id)
    if (refusal) setDeleteError(refusal)
    else navigate('/networks')
  }
  const onIsolation = async (next: boolean) => {
    if (!id) return
    setIsolationError(null)
    setIsolationBusy(true)
    try {
      await model.setIsolated(id, next)
    } catch (e) {
      console.error('network_update failed:', e)
      setIsolationError(toolRefusalMessage(e, NETWORK_FORBIDDEN, 'Could not change client isolation.'))
    } finally {
      setIsolationBusy(false)
    }
  }

  const onMembersMayAddNodes = async (next: boolean) => {
    if (!id) return
    setAddNodesError(null)
    setAddNodesBusy(true)
    try {
      await model.setMembersMayAddNodes(id, next)
    } catch (e) {
      console.error('network_update failed:', e)
      setAddNodesError(toolRefusalMessage(e, NETWORK_FORBIDDEN, 'Could not change who may add nodes.'))
    } finally {
      setAddNodesBusy(false)
    }
  }

  return (
    <Stack sx={{
      gap: 3
    }}>
      <BackListItem to="/networks">Networks</BackListItem>
      {/* The name IS the title, edited in place — the node page's pattern (app#177).
          A network name is a DNS label, so this carries `dnsLabel`: the same filter,
          lowercase and `REGEX_VALID_DOMAIN` that `network_create` already enforces
          router-side in ValidateDNSCompatibleName. Before this, create validated and
          rename did not, so the app could write a name it would have refused to
          create. */}
      <Box>
        <RenameTitle
          value={name ?? ''}
          subject="network"
          canRename={canManage}
          onRename={async next => {
            // `name` is undefined until the network lands in the store, and `id`
            // with it. Neither is a state a rename can be written from.
            if (!id || !name) return
            await model.rename(id, next)
          }}
          dnsLabel
          resetKey={networkId}
          slotProps={{ input: { sx: { typography: 'h1' } } }}
          sx={{ maxWidth: 420 }}
          readOnly={<Typography variant="h1">{name}</Typography>}
        />
      </Box>
      {!!id && (
        <Box>
          <Typography variant="h6">
            Nodes{' '}
            {/* ⚠ Offline nodes can be hidden, and then the total is not known: see `countLabel`.
                The space keeps "Nodes" and the count two words for a screen reader. */}
            <Typography component="span" variant="caption" color="textSecondary" sx={{ display: 'inline', marginLeft: 0.5 }}>
              {countLabel(memberCount, nodesComplete)}
            </Typography>
          </Typography>
          {/* All the members, with Detach on each row. The list scrolls, and the buttons below it
              stay in view. */}
          <ScrollList rows={8.5} rowHeight={MEMBER_ROW_HEIGHT}>
            {members.map(node => (
              <Stack key={node.id} direction="row" spacing={1} sx={{ alignItems: 'center', paddingRight: 0.5 }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <ListItemLink to={nodePagePath(node.id)} icon={<IconNode type="node" offline={isNodeOffline(node)} size={22} />}>
                    {nodeNameIn(node, id)}
                  </ListItemLink>
                </Box>
                {/* ⚠ Detach needs either side (PERMISSIONS.md § 5 Attachment): this network's owner
                    or an Admin, or the node's controllers. */}
                {(canManage || controls(node.ownerUserId)) && (
                  <DetachButton network={name} onClick={() => id && openAttach({ mode: 'detach', nodeId: node.id, networkId: id })} />
                )}
              </Stack>
            ))}
          </ScrollList>
          {/* ⚠ Stacked full width: side by side, both labels wrap to two lines at the default panel width. */}
          <Stack spacing={1} sx={{ marginTop: 1, alignItems: 'stretch' }}>
            {/* ⚠ Add Node needs a key, because it installs the agent with one. A compute node
                needs one too: the router enrolls the machine with the network's key, and
                `provision_node` refuses "provisioning not enabled for network X" when the network
                has none (router `tools/provisioning.go`, `GetNetworkToken`). So the button is off
                until a key exists, and says why. */}
            {!canAddNodes && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {ADD_NODE_REFUSED}
              </Typography>
            )}
            {!!networkTokens.length && canAddNodes && (
              <Button component={Link} to={`/node/${id}`} startIcon={<LuPlus />} size="small" variant="outlined" fullWidth sx={{ whiteSpace: 'nowrap' }}>
                Add Node
              </Button>
            )}
            {/* The nodes the reader controls, in the one dialog, which asks the router. */}
            {canAddNodes && (
              <Button startIcon={<LuPlus />} size="small" variant="outlined" fullWidth sx={{ whiteSpace: 'nowrap' }} onClick={() => openAttach({ mode: 'attach', networkId: id })}>
                Attach existing node
              </Button>
            )}
            {/* ⚠ A disabled button fires no pointer events, so the Tooltip needs the <span>. */}
            <Tooltip title={!canAddNodes ? ADD_NODE_REFUSED : networkTokens.length ? '' : 'Create a key for this network first'}>
              <span>
                <Button
                  component={Link}
                  to={`/compute/${id}`}
                  startIcon={<LuPlus />}
                  size="small"
                  variant="outlined"
                  disabled={!can('node.write') || !canAddNodes || !networkTokens.length}
                  fullWidth
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Compute node
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      )}
      {!!id && <StorageSection tree="networks" id={id} />}
      {usage && (
        <Box>
          <Typography variant="h6">Traffic</Typography>
          {/* Gate the matrix on the network's own usage row, not on networkTraffic: a transient
              get_node_traffic failure leaves nodeTrafficAll null, and we still want the Traffic
              section (period + node count) rather than hiding it. The matrix carries the period
              in its corner cell once traffic loads; until then keep the label as a standalone caption. */}
          {networkTraffic ? (
            <TrafficMatrix traffic={networkTraffic} caption={periodLabel(period)} />
          ) : (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block'
              }}>
              {periodLabel(period)}
            </Typography>
          )}
          {/* Storage is a level at rest, not traffic — it moved to the Storage section above, as the
              CURRENT size. The period PEAK that used to sit here is a billing input and still shows
              in the /usage network drill-down, which is where the rest of billing lives. */}
          <Typography variant="caption" sx={{
            color: 'text.secondary'
          }}>
            {usage.nodeCountPeak} active nodes
          </Typography>
          {/* Where the ORG stands against its bandwidth allowance, and how much of that this
              network is responsible for (app#86). `networkTraffic` may be null on a transient
              get_node_traffic failure — pass null rather than 0 so the share line says nothing
              instead of claiming this network used none of the pool. */}
          <TrafficAllowance scope="network" scopeBytes={networkTraffic ? totalTraffic(networkTraffic) : null} />
        </Box>
      )}
      {/* The network's access, Org or Private (you and org admins) (PERMISSIONS.md § 4.1, D-20,
          app#375). It is set by the network owner or an org Owner/Admin: the same rule as Delete
          (§ 5 Network), which the router checks again. */}
      {!!id && <NetworkAccess key={id} networkId={id} networkName={name ?? ''} canChange={canManage} />}
      <Box>
        <Typography variant="h6">Keys</Typography>
        <List sx={{ marginBottom: networkTokens.length ? 2 : 0 }}>
          {networkTokens.map(token => (
            <ListItemLink key={token.id} to={`/networks/token/${token.id}`} icon={<LuKeyRound />}>
              {token.name}
              <TokenIndicator token={token} />
            </ListItemLink>
          ))}
        </List>
        {canManage && (
          <TokenInsert
            onCreate={async name => {
              if (!id) return
              setKeyError(null)
              try {
                const keyId = await tokenModel.create(id, name)
                navigate(`/networks/token/${keyId}`)
              } catch (e) {
                console.error('network_key_create failed:', e)
                setKeyError(toolRefusalMessage(e, NETWORK_FORBIDDEN, 'Could not create the key.'))
              }
            }}
          />
        )}
        {keyError && <Alert severity="error">{keyError}</Alert>}
      </Box>
      <ChannelSelect
        value={channelId ?? null}
        channels={channels}
        disabled={!canManage}
        error={channelError}
        onChange={async next => {
          if (!id) return
          setChannelError(null)
          try {
            await model.setChannel(id, next)
          } catch (e) {
            console.error('network_update failed:', e)
            setChannelError(toolRefusalMessage(e, NETWORK_FORBIDDEN, 'Could not change the release channel.'))
          }
        }}
      />
      {!!id && (
        <Box>
          <Typography variant="h6">Settings</Typography>
          {/* Client isolation (docs/PERMISSIONS.md § 6, router#1769): the network's owner or an org
              Owner/Admin changes it; everyone else reads it. */}
          <FormControlLabel
            control={
              <Switch
                checked={!!isolated}
                disabled={!canManage || isolationBusy}
                onChange={e => void onIsolation(e.target.checked)}
              />
            }
            label="Client isolation"
          />
          <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
            {isolationLine(!!isolated)}
          </Typography>
          {isolationError && <Alert severity="error" sx={{ mt: 1 }}>{isolationError}</Alert>}
          {/* "Members may add nodes" (docs/PERMISSIONS.md § 5 Network, router#1761): the network's
              owner or an org Owner/Admin changes it; everyone else reads it. ⚠ Unknown (a database
              before migration 0230) reads as on: the column default. */}
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Switch
                checked={membersMayAddNodes !== false}
                disabled={!canManage || addNodesBusy}
                onChange={e => void onMembersMayAddNodes(e.target.checked)}
              />
            }
            label="Members may add nodes"
          />
          <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
            {membersMayAddNodesLine(membersMayAddNodes)}
          </Typography>
          {addNodesError && <Alert severity="error" sx={{ mt: 1 }}>{addNodesError}</Alert>}
        </Box>
      )}
      <Box>
        <Typography variant="h6">Manage</Typography>
        <List>
          {canManage ? (
            // ⚠ The message says what a delete does NOW: it never deletes a node, and its nodes leave
            // the network (router#2139). "This will delete all keys and nodes" was the cascade. It says
            // what the delete can do to sharing (router#2200).
            <DeleteConfirm message={`This deletes the network, its keys and its shared drive. It never deletes a node: each node leaves this network and stays. A node with no other network keeps commands and files, and its overlay stops. ${NETWORK_DELETE_SHARING_LINE}`} onConfirm={onDelete}>
              <Button variant="outlined" color="error" startIcon={<LuTrash />} size="small">
                Delete Network
              </Button>
            </DeleteConfirm>
          ) : (
            <Tooltip title="Only the owner of this network, or an organization owner or admin, can delete it">
              <span>
                <Button variant="outlined" color="error" startIcon={<LuTrash />} size="small" disabled>
                  Delete Network
                </Button>
              </span>
            </Tooltip>
          )}
        </List>
        {deleteError && <Alert severity="error" sx={{ mt: 1 }}>{deleteError}</Alert>}
      </Box>
    </Stack>
  )
}
